import React, { useState, useEffect } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Textarea } from '../components/Textarea';
import { Input } from '../components/Input';
import { API_ENDPOINTS, getAuthHeaders } from '../config/api';

export const ApplyToReadPage = () => {
  const [applicationMessage, setApplicationMessage] = useState('');
  const [estimatedTimeline, setEstimatedTimeline] = useState('');
  const [previousExperience, setPreviousExperience] = useState('');
  const [manuscript, setManuscript] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Extract manuscript ID from URL - supports UUIDs
  const manuscriptId = window.location.pathname.match(/\/manuscripts\/([a-zA-Z0-9-]+)\/apply/)?.[1];

  // Test log to verify component loaded
  console.log('🔄 ApplyToReadPage component loaded with debug code');

  useEffect(() => {
    if (manuscriptId) {
      fetchManuscript();
    } else {
      setError('Invalid manuscript ID');
      setLoading(false);
    }
  }, [manuscriptId]);

  const fetchManuscript = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(API_ENDPOINTS.manuscripts.detail(manuscriptId!), {
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to load manuscript');
        setLoading(false);
        return;
      }

      // Transform backend data to frontend format
      const m = data.manuscript;
      const transformedManuscript = {
        id: m.id,
        title: m.title,
        author: {
          name: m.author?.displayName || 'Unknown Author',
        },
        genre: formatGenre(m.primaryGenre),
        wordCount: m.wordCount,
        timeline: {
          readingDeadline: m.betaDeadline ? new Date(m.betaDeadline).toISOString().split('T')[0] : 'TBD',
          estimatedWeeks: 'To be discussed',
          feedbackFormat: 'Overall summary',
        },
      };

      setManuscript(transformedManuscript);
      setLoading(false);
    } catch (err) {
      console.error('Fetch manuscript error:', err);
      setError('Network error. Please check your connection and try again.');
      setLoading(false);
    }
  };

  const formatGenre = (genre: string) => {
    const genreMap: Record<string, string> = {
      FANTASY: 'Fantasy',
      SCIENCE_FICTION: 'Science Fiction',
      MYSTERY: 'Mystery',
      THRILLER: 'Thriller',
      ROMANCE: 'Romance',
      LITERARY_FICTION: 'Literary Fiction',
      HISTORICAL_FICTION: 'Historical Fiction',
      HORROR: 'Horror',
      YOUNG_ADULT: 'Young Adult',
      MIDDLE_GRADE: 'Middle Grade',
      CONTEMPORARY: 'Contemporary',
      PARANORMAL: 'Paranormal',
      DYSTOPIAN: 'Dystopian',
      OTHER: 'Other',
    };
    return genreMap[genre] || genre;
  };

  const handleSubmit = async () => {
    console.log('🎯 handleSubmit called');
    console.log('📋 Form data:', {
      manuscriptId,
      applicationMessage: applicationMessage.substring(0, 50) + '...',
      estimatedTimeline,
      previousExperience: previousExperience ? 'provided' : 'empty'
    });

    if (!applicationMessage.trim()) {
      console.log('❌ Validation failed: applicationMessage is empty');
      window.alert('Please tell the author why you\'re interested in beta reading this manuscript.');
      return;
    }

    console.log('✅ Validation passed, submitting...');
    setSubmitting(true);

    const payload = {
      manuscriptId,
      whyInterested: applicationMessage,
      estimatedTimeline: estimatedTimeline,
      betaReadingExperience: previousExperience || null,
    };
    console.log('📦 Payload:', payload);
    console.log('🔗 URL:', API_ENDPOINTS.betaApplications.submit);

    try {
      console.log('🚀 Making POST request...');
      const response = await fetch(API_ENDPOINTS.betaApplications.submit, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      console.log('📨 Response status:', response.status);
      const data = await response.json();
      console.log('📥 Response data:', data);

      if (!response.ok) {
        console.log('❌ Request failed:', data.error);
        window.alert(data.error || 'Failed to submit application');
        setSubmitting(false);
        return;
      }

      console.log('✅ Application submitted successfully!');
      window.alert('Application submitted! The author will review your application and get back to you soon.');
      window.location.href = '/reader/applications';
    } catch (err) {
      console.error('❌ Submit application error:', err);
      window.alert('Network error. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />
      <main style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{
            fontSize: '40px',
            fontWeight: '400',
            marginBottom: '8px',
            color: theme.colors.textPrimary,
          }}>
            Apply to Beta Read
          </h1>
          {!loading && manuscript && (
            <p style={{
              fontSize: '18px',
              color: theme.colors.textSecondary,
            }}>
              {manuscript.title} by {manuscript.author.name}
            </p>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.textSecondary,
              }}>
                Loading manuscript details...
              </p>
            </div>
          </Card>
        )}

        {/* Error State */}
        {!loading && error && (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.error,
                marginBottom: '20px',
              }}>
                {error}
              </p>
              <Button onClick={fetchManuscript}>
                Try Again
              </Button>
            </div>
          </Card>
        )}

        {/* Application Form */}
        {!loading && !error && manuscript && (
          <>

        <Card style={{ marginBottom: '24px' }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '500',
            marginBottom: '16px',
            color: theme.colors.textPrimary,
          }}>
            Application Details
          </h2>

          <Textarea
            label="Why are you interested in beta reading this manuscript?"
            placeholder="Tell the author about your interest in this project, your relevant reading experience, and what you hope to provide as a beta reader..."
            value={applicationMessage}
            onChange={(e) => setApplicationMessage(e.target.value)}
            rows={8}
            required
            helperText="Share your enthusiasm and qualifications (required)"
          />

          <div style={{ marginTop: '24px' }}>
            <Input
              type="text"
              label="Estimated Timeline"
              placeholder="e.g., 3-4 weeks"
              value={estimatedTimeline}
              onChange={(e) => setEstimatedTimeline(e.target.value)}
              required
              helperText="How long will it take you to read and provide feedback?"
            />
          </div>

          <div style={{ marginTop: '24px' }}>
            <Textarea
              label="Previous Beta Reading Experience (Optional)"
              placeholder="Share any previous beta reading experience, what genres you've read, or any relevant background..."
              value={previousExperience}
              onChange={(e) => setPreviousExperience(e.target.value)}
              rows={5}
              helperText="This helps the author understand your experience level"
            />
          </div>

          <div style={{
            marginTop: '24px',
            padding: '16px',
            backgroundColor: theme.colors.backgroundAlt,
            borderRadius: theme.borderRadius.medium,
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '500',
              marginBottom: '12px',
              color: theme.colors.textPrimary,
            }}>
              Manuscript Details
            </h3>
            <ul style={{
              margin: 0,
              paddingLeft: '20px',
              fontSize: '14px',
              lineHeight: '1.8',
              color: theme.colors.textSecondary,
            }}>
              <li>Genre: {manuscript.genre}</li>
              <li>Word count: {manuscript.wordCount.toLocaleString()} words</li>
              <li>Reading deadline: {manuscript.timeline.readingDeadline}</li>
              <li>Estimated time: {manuscript.timeline.estimatedWeeks}</li>
              <li>Feedback format: {manuscript.timeline.feedbackFormat}</li>
            </ul>
          </div>
        </Card>

        <Card style={{
          backgroundColor: theme.colors.primaryLight,
          marginBottom: '24px',
        }}>
          <h3 style={{
            fontSize: '16px',
            fontWeight: '500',
            marginBottom: '12px',
            color: theme.colors.textPrimary,
          }}>
            Beta Reading Commitment
          </h3>
          <p style={{
            fontSize: '14px',
            lineHeight: '1.6',
            color: theme.colors.textSecondary,
            margin: 0,
          }}>
            By applying, you're committing to read the manuscript in its entirety and provide thoughtful, constructive feedback by the agreed-upon deadline. If you need to withdraw from your commitment, please communicate with the author as soon as possible.
          </p>
        </Card>

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '16px',
          marginTop: '24px',
        }}>
          <Button
            variant="ghost"
            onClick={() => window.location.href = `/manuscripts/${manuscriptId}/preview`}
            disabled={submitting}
          >
            ← Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit Application'}
          </Button>
        </div>
        </>
        )}
      </main>
      <Footer />
    </div>
  );
};
